import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom'
import BlogSidebar from '../BlogSidebar/BlogSidebar.jsx'
import VideoModal from '../ModalVideo/VideoModal'
import { useArticles } from '../../hooks/useAirtableArticles';

const ClickHandler = () => {
    window.scrollTo(10, 0);
}

const BlogList = (props) => {
    const [currentPage, setCurrentPage] = useState(1);
    const articlesPerPage = 5;
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const searchTerm = searchParams.get('search') || '';
    
    // Reset to page 1 when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);
    
    // Helper function to strip markdown and HTML
    const stripMarkdown = (text) => {
        if (!text) return '';
        // Remove images
        text = text.replace(/!\[.*?\]\(.*?\)/g, '');
        // Remove links but keep text
        text = text.replace(/\[([^\]]+)\]\(.*?\)/g, '$1');
        // Remove headers
        text = text.replace(/#{1,6}\s+/g, '');
        // Remove bold/italic
        text = text.replace(/(\*\*|__)(.*?)\1/g, '$2');
        text = text.replace(/(\*|_)(.*?)\1/g, '$2');
        // Remove HTML tags
        text = text.replace(/<[^>]*>/g, '');
        // Remove extra whitespace
        text = text.replace(/\s+/g, ' ').trim();
        return text;
    };
    
    // Fetch from Airtable
    const { articles, loading, error } = useArticles(20, true);
    
    // Filter articles based on search term
    const filteredArticles = articles ? articles.filter(article => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            article.title?.toLowerCase().includes(searchLower) ||
            article.subtitle?.toLowerCase().includes(searchLower) ||
            article.excerpt?.toLowerCase().includes(searchLower) ||
            article.content?.toLowerCase().includes(searchLower) ||
            article.author?.toLowerCase().includes(searchLower) ||
            (Array.isArray(article.category) ? 
                article.category.some(cat => cat.toLowerCase().includes(searchLower)) :
                article.category?.toLowerCase().includes(searchLower))
        );
    }) : [];
    
    // Pagination
    const indexOfLastArticle = currentPage * articlesPerPage;
    const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
    const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
    const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

    if (loading) {
        return (
            <section className="wpo-blog-pg-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading articles...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !articles || articles.length === 0) {
        return (
            <section className="wpo-blog-pg-section section-padding">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <div className="alert alert-info" role="alert">
                                <h3 className="mb-3">Blog Temporarily Unavailable</h3>
                                <p className="mb-3">We're experiencing technical difficulties loading our blog articles.</p>
                                <p>Please check back later or contact us for more information.</p>
                                <Link to="/contact" className="theme-btn mt-3">
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="wpo-blog-pg-section section-padding">
            <div className="container">
                <div className="row">
                    <div className={`col col-lg-8 col-12 ${props.blRight}`}>
                        <div className="wpo-blog-content">
                            {searchTerm && (
                                <div className="search-results-header mb-4 p-4 bg-light rounded border">
                                    <h3 className="text-primary mb-3">
                                        <i className="ti-search me-2"></i>
                                        Search Results for: <strong>"{searchTerm}"</strong>
                                    </h3>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <p className="mb-0 text-muted">
                                            <strong>{filteredArticles.length}</strong> {filteredArticles.length === 1 ? 'article' : 'articles'} found
                                        </p>
                                        <Link to="/blog" className="btn btn-sm btn-outline-primary">
                                            <i className="ti-close me-1"></i>Clear Search
                                        </Link>
                                    </div>
                                    {filteredArticles.length === 0 && (
                                        <div className="alert alert-warning mt-3 mb-0">
                                            <i className="ti-info-alt me-2"></i>
                                            No articles match your search. Try different keywords or browse all articles.
                                        </div>
                                    )}
                                </div>
                            )}
                            {currentArticles.map((article) => {
                                const displayImage = article.featuredImage || 
                                    (article.images && article.images.length > 0 ? article.images[0].url : null);
                                
                                return (
                                    <div className="post" key={article.id}>
                                        {displayImage && (
                                            <div className="entry-media">
                                                <img 
                                                    src={displayImage} 
                                                    alt={article.title} 
                                                    style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                                                />
                                            </div>
                                        )}
                                    <div className="entry-meta">
                                        <ul>
                                            <li>
                                                <i className="fi flaticon-user"></i> 
                                                By <Link onClick={ClickHandler} to={`/blog/${article.slug}`}>
                                                    {article.author}
                                                </Link>
                                            </li>
                                            <li>
                                                <i className="fi flaticon-calendar"></i> 
                                                {new Date(article.publishedDate).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </li>
                                            {article.readTime && (
                                                <li>
                                                    <i className="fi flaticon-deadline"></i> 
                                                    {article.readTime}
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                    <div className="entry-details">
                                        <h3>
                                            <Link onClick={ClickHandler} to={`/blog/${article.slug}`}>
                                                {article.title}
                                            </Link>
                                        </h3>
                                        <p>
                                            {article.excerpt || article.subtitle || (article.content ? stripMarkdown(article.content).substring(0, 200) + '...' : 'Click to read more about this topic...')}
                                        </p>
                                        <Link onClick={ClickHandler} to={`/blog/${article.slug}`} className="read-more">
                                            READ MORE...
                                        </Link>
                                    </div>
                                </div>
                                )
                            })}

                            {totalPages > 1 && (
                                <div className="pagination-wrapper pagination-wrapper-left">
                                    <ul className="pg-pagination">
                                        <li className={currentPage === 1 ? 'disabled' : ''}>
                                            <button 
                                                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                                                aria-label="Previous"
                                                disabled={currentPage === 1}
                                                style={{ background: 'none', border: 'none', cursor: currentPage === 1 ? 'default' : 'pointer' }}
                                            >
                                                <i className="fi ti-angle-left"></i>
                                            </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, index) => (
                                            <li key={index + 1} className={currentPage === index + 1 ? 'active' : ''}>
                                                <button 
                                                    onClick={() => setCurrentPage(index + 1)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                >
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={currentPage === totalPages ? 'disabled' : ''}>
                                            <button 
                                                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                                                aria-label="Next"
                                                disabled={currentPage === totalPages}
                                                style={{ background: 'none', border: 'none', cursor: currentPage === totalPages ? 'default' : 'pointer' }}
                                            >
                                                <i className="fi ti-angle-right"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                    <BlogSidebar blLeft={props.blLeft} />
                </div>
            </div>
        </section>

    )

}

export default BlogList;
